import React, {useEffect, useMemo, useState } from 'react'
import {useTranslation} from 'react-i18next'

import {
    Dialog,
    DialogType,
    DialogFooter,
    PrimaryButton,
    ContextualMenuItemType,
    DefaultButton,
    DetailsListLayoutMode,
    DirectionalHint,
    IColumn,
    IconButton,
    IContextualMenuProps,
    Modal,
    SelectionMode,
    Separator,
    ShimmeredDetailsList,
    Spinner,
    SpinnerSize,
    Stack,
    Text
} from "@fluentui/react"
import {ApprovalOverride, contentStyles, iconButtonStyles} from "./index"
import {useBranding} from "../../hooks/useBranding"
import StatusMessage, {StatusEnum} from "../../share/StatusMessage"
import {IDocument} from "../../App"
import ApprovalService, {
    IPostApprovalRequest,
    IGetApprovalActionResponseRequest,
    IGetApprovalActionResponseResponse,
    IGetApprovalStepActionStateRequest,
    IGetApprovalStepActionStateResponse,
    IPostApprovalStepActionStateRequest
} from "../../services/Approval"
import {formatFromUTC, formatSimpleDate} from "../../Helpers/DateUtils"
import Helper from "../../Helpers/Helper"
import {EApprovalCommand} from "./Approval.data";
import {decode} from 'html-entities'
import FormTemplateLinkService from "../TemplateLink/FormTemplateLink.service";
import {DocumentOnlyTemplateServices} from "../EmailTemplate";

type Properties = {
    document: IDocument | undefined
    onDismissModal: () => void,
    showModal: boolean
    updateWorkspace: () => void
}

export function ApprovalStepActionStatus({
                                             showModal,
                                             onDismissModal,
                                             document,
                                             updateWorkspace
                                         }: Properties) {

    const {t,} = useTranslation(['approval', 'common'])
    const {branding} = useBranding()

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmittingReopen, setIsSubmittingReopen] = useState(false);
    const [messageApprove, setMessageApprove] = useState("")
    const [showMessageApprove, setShowMessageApprove] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isLoadingActions, setIsLoadingActions] = useState(false)
    const [showApprovalModal, setShowApprovalModal] = useState(false)
    const [titleApprovalModal, setTitleApprovalModal] = useState("")
    const [statusMessageApprove, setStatusMessageApprove] = useState<StatusEnum>(StatusEnum.Info)
    const [hasTimerMessageApprove, setHasTimerMessageApprove] = useState(false)
    const [items, setItems] = useState<IGetApprovalStepActionStateResponse[]>([])
    const [currentAction, setCurrentAction] = useState<IGetApprovalStepActionStateResponse | null>(null)
    const [actionsResponse, setActionsResponse] = useState<IGetApprovalActionResponseResponse[]>([])
    const [overrideCommand, setOverrideCommand] = useState<EApprovalCommand>(EApprovalCommand.Abort)
    const [shouldUpdateWorkspace, setShouldUpdateWorkspace] = useState(false)
    const [presentInAudience, setPresentInAudience] = useState(false)
    const [isLoadingAudiences, setIsLoadingAudiences] = useState(false)

    useEffect(() => {
        if (showModal) {
            init().then()
            loadAudience().then()
        }
    }, [showModal])


    useEffect(() => {
        if (items.length > 0) {
            loadActionResponses().then()
        }
    }, [items])


    function OnBeforeDismissModal() {
        if (shouldUpdateWorkspace) {
            updateWorkspace()
        }
        onDismissModal()
    }

    async function ResendApproval() {

        if (isSubmitting) return;
        setIsSubmitting(true);

        const request: IPostApprovalRequest = {
            Active: false,
            Description: '',
            Name: '',
            TemplateId: String(document?.TemplateId ?? '0'),
            DocumentId: String(document?.Id ?? '0'),
            Resend: true
        }

        await ApprovalService.postApproval(request)
            .then(data => {
                if (data.Success) {
                    ShowMessageApprove(data.Result, StatusEnum.Success, true);
                }

            })
            .catch((error) => {
                console.error(error);
                ShowMessageApprove("Resend Failed", StatusEnum.Error, true);
            })
            .finally(() => {
                setIsSubmitting(false);
            })
    }

    const [showReopenDialog, setShowReopenDialog] = useState(false);

    const onConfirmReopen = async () => {
        setShowReopenDialog(false);
        await ReopenApproval();
    };


    async function ReopenApproval() {

        if (isSubmittingReopen)
        {
            return;
        }

        setIsSubmittingReopen(true);

        const request: IPostApprovalStepActionStateRequest = {
            TemplateId: String(document?.TemplateId ?? '0'),
            DocumentId: String(document?.Id ?? '0'),
            Operation: "Reopen"
        }

        await ApprovalService.postApprovalStepActionState(request)
            .then(async data => {
                if (data.Success) {
                    ShowMessageApprove(data.Result, StatusEnum.Success, true);
                    await init();
                    //Uncomment to update the workspace once the stored procedure is faster.
                    //setShouldUpdateWorkspace(true);
                    //updateWorkspace();
                }
                else {
                    ShowMessageApprove("Reopen Failed! " + data?.ExceptionMessage, StatusEnum.Error, true);
                }

            })
            .catch((error) => {
                console.error(error);
                ShowMessageApprove("Reopen Failed", StatusEnum.Error, true);
            })
            .finally(() => {
                setIsSubmittingReopen(false);
            })
    }

    /**
     * Displays the approval override modal by setting the modal's visibility state.
     *
     * @return {void} This function does not return a value.
     */
    function ShowApprovalOverride(command: EApprovalCommand) {
        setOverrideCommand(command)
        setShowApprovalModal(true)
    }

    const onDismissApprovalModal = (update: boolean = false) => {
        setShowApprovalModal(false)
        setShouldUpdateWorkspace(update)
        if (update) {
            ShowMessageApprove(t('message.successUpdated', {ns: 'common'}), StatusEnum.Success, true)
            init().then()
        }
    }

    function IsCompleteOrAbort(currentAction: IGetApprovalStepActionStateResponse | null) {
        if (currentAction !== null) {
            return currentAction.DoAction.toLowerCase().trim() === "complete" || currentAction.DoAction.toLowerCase().trim() === "abort"
        }
        return false

    }

    const menuOverrideProps: IContextualMenuProps = useMemo(() => {

        let commnand1 = EApprovalCommand.PreviousStep
        let commnand2 = EApprovalCommand.PreviousStep

        let nextStepText = t('text.commandAdvance', {ns: 'approval'})

        const onNext = actionsResponse.find(action => action.DoActionType.toLowerCase().trim() === "command" && action.DoAction.toLowerCase().trim() === "nextstep")
        if (onNext) {
            switch (onNext.OnEvent) {
                case 'OnApprove':
                    nextStepText = `${t('text.approve', {ns: 'approval'})} (${t('text.commandAdvance', {ns: 'approval'})})`
                    commnand1 = EApprovalCommand.NextStep
                    break
                case 'OnReject':
                    nextStepText = `${t('text.reject', {ns: 'approval'})} (${t('text.commandAdvance', {ns: 'approval'})})`
                    commnand1 = EApprovalCommand.PreviousStep
                    break
            }
        }

        let previousStepText = t('text.commandPrevious', {ns: 'approval'})
        const onPrevious = actionsResponse.find(action => action.DoActionType.toLowerCase().trim() === "command" && action.DoAction.toLowerCase().trim() === "previousstep")
        if (onPrevious) {
            switch (onPrevious.OnEvent) {
                case 'OnApprove':
                    previousStepText = `${t('text.approve', {ns: 'approval'})} (${t('text.commandPrevious', {ns: 'approval'})})`
                    commnand2 = EApprovalCommand.NextStep
                    break
                case 'OnReject':
                    previousStepText = `${t('text.reject', {ns: 'approval'})} (${t('text.commandPrevious', {ns: 'approval'})})`
                    commnand2 = EApprovalCommand.PreviousStep
                    break
            }
        }

        const items: IContextualMenuProps = {
            items: [
                {
                    key: 'complete',
                    text: t('text.commandComplete', {ns: 'approval'}),
                    iconProps: {iconName: 'StatusCircleCheckmark'},
                    disabled: IsCompleteOrAbort(currentAction),
                    onClick: () => {
                        setTitleApprovalModal(t('text.commandComplete', {ns: 'approval'}))
                        ShowApprovalOverride(EApprovalCommand.Complete)
                    }
                },
                {
                    key: 'abort',
                    text: t('text.commandAbort', {ns: 'approval'}),
                    iconProps: {iconName: 'StatusCircleBlock'},
                    disabled: IsCompleteOrAbort(currentAction),
                    onClick: () => {
                        setTitleApprovalModal(t('text.commandAbort', {ns: 'approval'}))
                        ShowApprovalOverride(EApprovalCommand.Abort)
                    }
                },
                {
                    key: 'divider',
                    itemType: ContextualMenuItemType.Divider
                },
                {
                    key: 'next',
                    text: nextStepText,
                    iconProps: {iconName: 'IncreaseIndentArrow'},
                    disabled: IsCompleteOrAbort(currentAction) || !actionsResponse.some(action => action.DoActionType.toLowerCase().trim() === "command" && action.DoAction.toLowerCase().trim() === "nextstep"),
                    onClick: () => {
                        setTitleApprovalModal(nextStepText)
                        ShowApprovalOverride(commnand1)
                    }
                },
                {
                    key: 'previous',
                    text: previousStepText,
                    iconProps: {iconName: 'DecreaseIndentArrow'},
                    disabled: IsCompleteOrAbort(currentAction) || !actionsResponse.some(action => action.DoActionType.toLowerCase().trim() === "command" && action.DoAction.toLowerCase().trim() === "previousstep"),
                    onClick: () => {
                        setTitleApprovalModal(previousStepText)
                        ShowApprovalOverride(commnand2)
                    }
                },

            ]
            ,
            directionalHintFixed: true,
            directionalHint: DirectionalHint.topAutoEdge
        }
        return items

    }, [actionsResponse])

    const init = async () => {

        setIsLoading(true)
        setItems([])

        const request: IGetApprovalStepActionStateRequest = {
            Id: String(document?.Id ?? '0'),
            Mode: "documentid",
            TemplateId: String(document?.TemplateId ?? '0')
        }


        await ApprovalService.getApprovalStepActionState(request)
            .then(data => {
                try {
                    const resultData = JSON.parse(data.Result) as Array<IGetApprovalStepActionStateResponse>

                    if (Array.isArray(resultData)) {
                        resultData.sort((a, b) => a.Id - b.Id)
                        setItems(resultData)

                        if (resultData.length > 0) {
                            setCurrentAction(resultData[resultData.length - 1])
                        }


                    } else {
                        ShowMessageApprove(t('message.notFound', {ns: 'common'}), StatusEnum.Error)
                    }
                } catch (error) {
                    ShowMessageApprove(t('message.error', {ns: 'common', Error: error.toString()}), StatusEnum.Error)
                }
            })
            .catch(error => {
                ShowMessageApprove(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }


    const loadAudience = async () => {
        setPresentInAudience(false)
        setIsLoadingAudiences(true)

        await DocumentOnlyTemplateServices.getDocumentOnlyTemplateIDBasic({
            TemplateId: String(document?.TemplateId ?? ''),
            IgnoreDocument: true
        })
            .then(async (result) => {


                    await FormTemplateLinkService.GetDocumentOnlyAudienceDetailByAudienceMasterId({
                        AvoidCache: true,
                        AudienceMasterId: String(result.DocumentOnlyAudienceId ?? '')
                    })
                        .then(data => {
                            if (Array.isArray(data)) {

                                const audience = data.find(x => x.UserName?.trim().toLowerCase() === Helper.getUsername()?.trim().toLowerCase() && (x.Owner || x.CoOwner))
                                if (audience) {
                                    setPresentInAudience(true)
                                }
                            }
                        })
                        .catch()
                        .finally()
                }
            )
            .finally(() => {
                setIsLoadingAudiences(false)
            })
    }


    /**
     * Load init
     */
    const loadActionResponses = async () => {

        setIsLoadingActions(true)
        setActionsResponse([])

        const _currentAction: IGetApprovalStepActionStateResponse = items[items.length - 1]

        const request: IGetApprovalActionResponseRequest = {
            TemplateId: String(_currentAction!.TemplateId),
            Mode: 'approvalStepId',
            Id: String(_currentAction!.ApprovalStepId)
        }

        await ApprovalService.getApprovalActionResponse(request)
            .then(data => {
                if (data.Success) {
                    const resultData = JSON.parse(data.Result) as Array<IGetApprovalActionResponseResponse>

                    if (Array.isArray(resultData)) {
                        setActionsResponse(resultData)
                    }
                }

            })
            .catch((_) => {

            })
            .finally(() => {
                setIsLoadingActions(false)
            })

    }

    /**
     * Format date
     * @param date
     * @param format
     * @param utc
     */
    const onFormatDate = (date?: Date, format: string = "ll", utc: boolean = false): string => {
        const _locale = !!sessionStorage.getItem("language")
            ? sessionStorage.getItem("language")!.toString()
            : "en"
        if (utc) return date !== null ? formatFromUTC(date, format, _locale) : null
        else return date !== null ? formatSimpleDate(date, format, _locale) : null
    }


    /**
     * Displays an approval message with a specified status.
     *
     * @param {string} message - The message content to be displayed.
     * @param {StatusEnum} status - The status associated with the approval message.
     * @param hasTimer
     * @return {void} This function does not return a value.
     */

    function ShowMessageApprove(message: string, status: StatusEnum, hasTimer: boolean = false): void {
        setMessageApprove(decode(message))
        setStatusMessageApprove(status)
        setHasTimerMessageApprove(hasTimer)
        setShowMessageApprove(true)
    }


    const approvalNotesColumns: IColumn[] = [
        {
            key: 'column1',
            name: t('title.colStepName', {ns: 'approval'}),
            fieldName: 'ApprovalStepName',
            minWidth: 120,
            maxWidth: 120,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalStepActionStateResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%",
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                        }
                    }}>
                        <Text variant={"medium"}
                              title={item.ApprovalStepName}
                        >
                            {item.ApprovalStepName}
                        </Text>
                    </Stack>
                </>
            }
        },
        {
            key: 'column2',
            name: t('title.colEmailAddress', {ns: 'approval'}),
            fieldName: 'EmailAddress',
            minWidth: 140,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalStepActionStateResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        <Text variant={"medium"}
                              styles={{
                                  root: {
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      width: '100%',
                                  }
                              }}
                              block
                              title={item.EmailAddress}
                        >
                            {item.EmailAddress}
                        </Text>
                    </Stack>
                </>
            }
        },
        {
            key: 'column3',
            name: t('title.colResponseAction', {ns: 'approval'}),
            fieldName: 'ApprovalStepActionNameResponse',
            minWidth: 120,
            maxWidth: 120,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalStepActionStateResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        <Text variant={"medium"}
                              styles={{
                                  root: {
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      width: '100%',

                                  }
                              }}
                              block
                              title={item.ApprovalStepActionNameResponse}
                        >
                            {item.ApprovalStepActionNameResponse}
                        </Text>
                    </Stack>
                </>
            }
        },
        {
            key: 'column4',
            name: t('title.colEvent', {ns: 'approval'}),
            fieldName: 'OnEvent',
            minWidth: 120,
            maxWidth: 120,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalStepActionStateResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        <Text variant={"medium"}
                              styles={{
                                  root: {
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      width: '100%'
                                  }
                              }}
                              block
                              title={item.DoAction}
                        >
                            {item.OnEvent !== null && item.OnEvent !== undefined && item.OnEvent.startsWith('On')
                                ? item.OnEvent.slice(2)
                                : item.OnEvent}
                        </Text>
                    </Stack>
                </>
            }
        },
        {
            key: 'column5',
            name: t('title.colDateApprovalSent', {ns: 'approval'}),
            fieldName: 'DateApprovalSent',
            minWidth: 160,
            maxWidth: 160,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalStepActionStateResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>

                        {item.DateApprovalSent === null ?
                            ' '
                            :
                            <Text block variant={"medium"}
                                  styles={{
                                      root: {
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          width: '100%',

                                      }
                                  }}
                                  title={onFormatDate(new Date(item.DateApprovalSent), "lll")}>
                                {onFormatDate(new Date(item.DateApprovalSent), "ll", true)}
                            </Text>
                        }
                    </Stack>
                </>
            }
        },
        {
            key: 'column6',
            name: t('title.colDateApprovalReceived', {ns: 'approval'}),
            fieldName: 'DateResponseReceived',
            minWidth: 160,
            maxWidth: 160,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalStepActionStateResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        {item.DateResponseReceived === null ?
                            ' '
                            :
                            <Text block variant={"medium"}

                                  styles={{
                                      root: {
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          width: '100%'
                                      }
                                  }}

                                  title={onFormatDate(new Date(item.DateResponseReceived), "lll")}>
                                {onFormatDate(new Date(item.DateResponseReceived), "ll", true)}
                            </Text>
                        }
                    </Stack>
                </>
            }
        }

    ]


    return (
        <>
            <Modal
                isOpen={showModal}
                isBlocking={true}
                containerClassName={contentStyles(branding.theme).containerApprovalNote}
            >

                <Stack grow={1} styles={{root: {height: "100%"}}}>
                    <Stack tokens={{childrenGap: 8}} styles={{
                        root: {
                            height: 56,
                            alignItems: 'center'
                        }
                    }} horizontal>

                        <Stack grow={1}
                               styles={{
                                   root: {
                                       textOverflow: "ellipsis",
                                       paddingLeft: 16,
                                       whiteSpace: "nowrap",
                                       overflow: "hidden"
                                   }
                               }}>
                            <Text variant={"xLarge"}
                                  title={t('title.approvalState', {
                                      ns: 'approval',
                                      Name: Helper.AddParentheses(document?.Description)
                                  })}>
                                {t('title.approvalState', {
                                    ns: 'approval',
                                    Name: Helper.AddParentheses(document?.Description)
                                })}
                            </Text>
                        </Stack>

                        <Stack horizontal styles={{root: {alignItems: "center", paddingRight: 8}}}>
                            <IconButton
                                styles={iconButtonStyles}
                                iconProps={{iconName: 'Cancel'}}
                                ariaLabel={t('Common.Button.Close', {ns: 'common'})}
                                onClick={OnBeforeDismissModal}
                                title={t('Common.Button.Close', {ns: 'common'})}
                            />
                        </Stack>
                    </Stack>
                    <Stack grow={1} styles={{root: {height: 1}}}>

                        <Stack horizontal styles={{root: {height: '100%'}}}>

                            <Stack grow={1} styles={{root: {height: '100%', width: 1}}}>

                                {/*-- START CONTENT --*/}
                                <Stack grow={1} styles={{root: {overflow: 'auto', paddingLeft: 16, paddingRight: 16}}}
                                       className="scrollVisible">

                                    {showMessageApprove &&
                                        <>
                                            <Stack styles={{root: {paddingTop: 8}}}>
                                                <StatusMessage status={statusMessageApprove}
                                                               hasTimer={hasTimerMessageApprove}
                                                               truncated={true}
                                                               isMultiline={true}
                                                               dismiss={true}

                                                               setShowMessage={setShowMessageApprove}>
                                                    <div>{messageApprove}</div>
                                                </StatusMessage>
                                            </Stack>
                                        </>
                                    }

                                    <Stack styles={{root: {width: 1024}}}>
                                        <ShimmeredDetailsList
                                            items={items}
                                            enableShimmer={isLoading}
                                            compact={false}
                                            columns={approvalNotesColumns}
                                            selectionMode={SelectionMode.none}
                                            layoutMode={DetailsListLayoutMode.fixedColumns}
                                            isHeaderVisible={true}
                                            isSelectedOnFocus={false}
                                            setKey={"Id"}
                                        />
                                    </Stack>

                                </Stack>
                                {/*-- END CONTENT --*/}

                                {/*-- START FOOTER --*/}
                                <Separator></Separator>
                                <Stack horizontal tokens={{childrenGap: 8}}
                                       styles={{root: {paddingRight: 16, paddingBottom: 12, alignItems: 'center'}}}>
                                    <Stack grow={1}>&nbsp;</Stack>

                                    {isLoadingAudiences &&
                                        <Stack>
                                            <Spinner size={SpinnerSize.medium} labelPosition="left"
                                                     label={t('text.spinner', {ns: 'common'})}/>
                                        </Stack>
                                    }

                                    {presentInAudience &&
                                        <DefaultButton
                                            iconProps={{iconName: 'PencilReply'}}
                                            menuProps={menuOverrideProps}
                                            styles={{root: {marginRight: 8}}}
                                            persistMenu
                                            text={t('text.override', {ns: 'approval'})}
                                            title={t('text.override', {ns: 'approval'})}
                                            disabled={isLoading || isLoadingActions || actionsResponse.length === 0}
                                        />
                                    }

                                    <Stack horizontal styles={{ root: { alignItems: "center", paddingRight: 8 } }}>
                                        <DefaultButton
                                            styles={{ root: { marginRight: 8 } }}
                                            iconProps={{ iconName: 'Send' }}
                                            text={t('Common.Button.Resend', { ns: 'common' })}
                                            onClick={ResendApproval}
                                            title={t('Common.Button.Resend', { ns: 'common' })}

                                        >
                                            {isSubmitting ? "Processing..." : "Resend Approval"}
                                        </DefaultButton>
                                    </Stack>

                                    <Stack horizontal styles={{ root: { alignItems: "center", paddingRight: 8 } }}>
                                        <DefaultButton
                                            styles={{ root: { marginRight: 8 } }}
                                            iconProps={{ iconName: "Redo" }}
                                            text={t("Common.Button.ReopenApproval", { ns: "common" })}
                                            onClick={() => setShowReopenDialog(true)}
                                            title={t("Common.Button.ReopenApproval", { ns: "common" })}
                                        >
                                            {isSubmittingReopen ? "Processing..." : "Reopen Approval"}
                                        </DefaultButton>
                                    </Stack>

                                    <DefaultButton
                                        onClick={OnBeforeDismissModal}
                                        styles={{root: {marginRight: 8}}}
                                        text={t('Common.Button.Close', {ns: 'common'})}
                                        title={t('Common.Button.Close', {ns: 'common'})}/>

                                </Stack>
                                {/*-- END FOOTER --*/}

                            </Stack>

                        </Stack>
                    </Stack>
                </Stack>

            </Modal>

            <Dialog
                hidden={!showReopenDialog}
                onDismiss={() => setShowReopenDialog(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: "Reopen Approval",
                    subText: "This action will reopen the approval process. Do you want to continue?"
                }}
            >
                <DialogFooter>
                    <PrimaryButton
                        text={t("Common.Button.Yes", { ns: "common" })}
                        onClick={onConfirmReopen}
                    />
                    <DefaultButton
                        text={t("Common.Button.No", { ns: "common" })}
                        onClick={() => setShowReopenDialog(false)}
                    />
                </DialogFooter>
            </Dialog>


            <ApprovalOverride
                command={overrideCommand}
                showModal={showApprovalModal}
                onDismissModal={onDismissApprovalModal}
                currentAction={currentAction}
                title={titleApprovalModal}
            />
        </>
    )
}


